import React from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import publicApi from '../services/public.api'

const CartItemProductInfo = ({ item, compact = false }) => {
    const getProductImageUrl = (product) => {
        if (!product || !product.image) return 'assets/images/thumbs/product-two-img1.png';
        return publicApi.getImageUrl(product.image);
    };

    const thumbSize = compact ? 72 : 80;
    const linkBoxSize = compact ? 80 : 100;

    return (
        <div className={`table-product d-flex align-items-start gap-16 ${compact ? 'cart-item-card__product' : 'align-items-center gap-24'}`}>
            <Link
                to={item.isPack ? `/shop` : `/product-details/${item.id}`}
                className="table-product__thumb border border-gray-100 rounded-8 flex-center flex-shrink-0"
                style={{ width: `${linkBoxSize}px`, height: `${linkBoxSize}px` }}
            >
                <img
                    src={getProductImageUrl(item)}
                    alt={item.name}
                    style={{ width: `${thumbSize}px`, height: `${thumbSize}px`, objectFit: 'contain' }}
                    onError={(e) => { e.target.src = 'assets/images/thumbs/product-two-img1.png'; }}
                />
            </Link>
            <div className="table-product__content text-start min-w-0">
                <h6 className={`title fw-semibold mb-8 ${compact ? 'text-md' : 'text-lg'}`}>
                    <Link
                        to={item.isPack ? `/shop` : `/product-details/${item.id}`}
                        className="link text-line-2"
                    >
                        {item.name}
                        {item.isPack && (
                            <span className="badge bg-main-600 text-white ms-8 text-xs">Pack</span>
                        )}
                        {item.weightLabel && (
                            <span className="badge bg-gray-100 text-heading ms-8 text-xs">{item.weightLabel}</span>
                        )}
                    </Link>
                </h6>
                {item.isPack && item.packProducts && item.packProducts.length > 0 && (
                    <div className="mt-8">
                        <span className="text-gray-500 text-sm d-block mb-8">
                            {item.packProducts.length} produits inclus:
                        </span>
                        <div className="d-flex flex-wrap gap-4">
                            {item.packProducts.map((product, idx) => (
                                <div
                                    key={idx}
                                    className="border border-gray-100 rounded-4 p-4"
                                    style={{ width: '36px', height: '36px' }}
                                    title={product.name}
                                >
                                    <img
                                        src={publicApi.getImageUrl(product.image)}
                                        alt={product.name}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        onError={(e) => { e.target.src = 'assets/images/thumbs/product-two-img1.png'; }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {!item.isPack && item.category && (
                    <span className="text-gray-500 text-sm d-block">
                        {item.category}
                    </span>
                )}
                {item.unit && (
                    <span className="text-gray-400 text-sm">
                        / {item.unit}
                    </span>
                )}
            </div>
        </div>
    );
};

const CartQuantityControls = ({ item, incrementQuantity, decrementQuantity, getCartItemKey }) => (
    <div className="d-flex align-items-center gap-8">
        <button
            type="button"
            onClick={() => decrementQuantity(getCartItemKey(item))}
            className="w-32 h-32 flex-center border border-gray-100 rounded-4 hover-bg-main-600 hover-text-white cart-quantity-btn"
            style={{ cursor: 'pointer' }}
            aria-label="Diminuer"
        >
            <i className="ph ph-minus" />
        </button>
        <span className="w-40 text-center fw-semibold">
            {item.quantity}
        </span>
        <button
            type="button"
            onClick={() => incrementQuantity(getCartItemKey(item))}
            className="w-32 h-32 flex-center border border-gray-100 rounded-4 hover-bg-main-600 hover-text-white cart-quantity-btn"
            style={{ cursor: 'pointer' }}
            aria-label="Augmenter"
        >
            <i className="ph ph-plus" />
        </button>
    </div>
);

const CartSection = () => {
    const {
        cartItems,
        removeFromCart,
        incrementQuantity,
        decrementQuantity,
        getCartTotal,
        getCartTax,
        getCartShipping,
        getCartGrandTotal,
        clearCart,
        getItemPrice,
        getCartItemKey,
    } = useCart();

    const getSubtotal = (item) => (getItemPrice(item) * item.quantity).toFixed(2);

    const EmptyCart = () => (
        <div className="text-center py-80">
            <div className="mb-24">
                <i className="ph ph-shopping-cart text-gray-300" style={{ fontSize: '100px' }}></i>
            </div>
            <h4 className="text-gray-600 mb-16">Votre panier est vide</h4>
            <p className="text-gray-500 mb-32">
                Parcourez notre boutique et ajoutez des produits à votre panier.
            </p>
            <Link to="/shop" className="btn btn-main py-16 px-32 rounded-8">
                Continuer les achats
            </Link>
        </div>
    );

    const CartActions = () => (
        <div className="flex-between flex-wrap gap-16 mt-16">
            <div className="flex-align gap-16 w-100 w-lg-auto">
                <input
                    type="text"
                    className="common-input cart-input flex-grow-1"
                    placeholder="Code promo"
                />
                <button
                    type="button"
                    className="btn btn-main py-16 px-24 rounded-8 flex-shrink-0"
                >
                    Appliquer
                </button>
            </div>
            <button
                type="button"
                onClick={clearCart}
                className="cart-update-btn text-lg text-gray-500 hover-text-danger-600"
            >
                <i className="ph ph-trash me-8" />
                Vider le panier
            </button>
        </div>
    );

    const CartSidebar = () => (
        <div className="cart-sidebar border border-gray-100 rounded-8 px-24 py-40">
            <h6 className="text-xl mb-32">Total du panier</h6>
            <div className="bg-color-three rounded-8 p-24">
                <div className="mb-32 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">Sous-total</span>
                    <span className="text-gray-900 fw-semibold">
                        {getCartTotal().toFixed(2)} DH
                    </span>
                </div>
                <div className="mb-32 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">
                        Livraison
                    </span>
                    <span className="text-gray-900 fw-semibold">
                        {getCartShipping() === 0 ? 'Gratuite' : `${getCartShipping().toFixed(2)} DH`}
                    </span>
                </div>
                <div className="mb-0 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">
                        TVA (20%)
                    </span>
                    <span className="text-gray-900 fw-semibold">
                        {getCartTax().toFixed(2)} DH
                    </span>
                </div>
            </div>
            <div className="bg-color-three rounded-8 p-24 mt-24">
                <div className="flex-between gap-8">
                    <span className="text-gray-900 text-xl fw-semibold">Total</span>
                    <span className="text-gray-900 text-xl fw-semibold">
                        {getCartGrandTotal().toFixed(2)} DH
                    </span>
                </div>
            </div>
            {getCartTotal() < 500 && (
                <div className="mt-16 p-16 bg-warning-50 rounded-8">
                    <p className="text-sm text-gray-700 mb-0">
                        <i className="ph ph-truck me-8" />
                        Ajoutez {(500 - getCartTotal()).toFixed(2)} DH pour bénéficier de la livraison gratuite !
                    </p>
                </div>
            )}
            <Link
                to="/checkout"
                className="btn track-order-btn mt-40 py-18 w-100 rounded-8"
            >
                Passer à la commande
            </Link>
            <Link
                to="/shop"
                className="btn btn-outline-gray mt-16 py-16 w-100 rounded-8 text-center"
            >
                Continuer les achats
            </Link>
        </div>
    );

    if (cartItems.length === 0) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <EmptyCart />
                </div>
            </section>
        );
    }

    return (
        <section className="cart py-80">
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-xl-9 col-lg-8">
                        <div className="cart-table border border-gray-100 rounded-8 px-16 px-lg-40 py-24 py-lg-48">
                            {/* Mobile: card layout */}
                            <div className="cart-items-mobile d-lg-none">
                                {cartItems.map((item) => (
                                    <div key={getCartItemKey(item)} className="cart-item-card border border-gray-100 rounded-12 p-16 mb-16">
                                        <CartItemProductInfo item={item} compact />
                                        <div className="cart-item-card__meta d-flex flex-wrap align-items-center justify-content-between gap-12 mt-16 pt-16 border-top border-gray-100">
                                            <div>
                                                <span className="text-gray-500 text-xs d-block mb-4">Prix unitaire</span>
                                                <span className="text-md fw-semibold text-heading">
                                                    {getItemPrice(item).toFixed(2)} DH
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 text-xs d-block mb-4">Quantité</span>
                                                <CartQuantityControls
                                                    item={item}
                                                    incrementQuantity={incrementQuantity}
                                                    decrementQuantity={decrementQuantity}
                                                    getCartItemKey={getCartItemKey}
                                                />
                                            </div>
                                            <div className="text-end">
                                                <span className="text-gray-500 text-xs d-block mb-4">Sous-total</span>
                                                <span className="text-md fw-semibold text-heading">
                                                    {getSubtotal(item)} DH
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeFromCart(getCartItemKey(item))}
                                            className="remove-tr-btn flex-align gap-8 hover-text-danger-600 mt-16 text-sm"
                                        >
                                            <i className="ph ph-x-circle text-xl d-flex" />
                                            Retirer
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop: table layout */}
                            <div className="cart-table-desktop d-none d-lg-block overflow-x-auto scroll-sm scroll-sm-horizontal">
                                <table className="table style-three">
                                    <thead>
                                        <tr>
                                            <th className="h6 mb-0 text-lg fw-bold">Supprimer</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Produit</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Prix</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Quantité</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Sous-total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cartItems.map((item) => (
                                            <tr key={getCartItemKey(item)}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFromCart(getCartItemKey(item))}
                                                        className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                                                    >
                                                        <i className="ph ph-x-circle text-2xl d-flex" />
                                                        Retirer
                                                    </button>
                                                </td>
                                                <td>
                                                    <CartItemProductInfo item={item} />
                                                </td>
                                                <td>
                                                    <span className="text-lg h6 mb-0 fw-semibold">
                                                        {getItemPrice(item).toFixed(2)} DH
                                                    </span>
                                                </td>
                                                <td>
                                                    <CartQuantityControls
                                                        item={item}
                                                        incrementQuantity={incrementQuantity}
                                                        decrementQuantity={decrementQuantity}
                                                        getCartItemKey={getCartItemKey}
                                                    />
                                                </td>
                                                <td>
                                                    <span className="text-lg h6 mb-0 fw-semibold">
                                                        {getSubtotal(item)} DH
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <CartActions />
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-4">
                        <CartSidebar />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CartSection
